import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { MapPinned, ChevronDown, ChevronUp, X } from "lucide-react";

const LEVELS = [["district", "District"], ["tehsil", "Tehsil"], ["ri", "R.I. Circle"], ["village", "Village"]];
export const EMPTY_LOCATION = { district: "", tehsil: "", ri: "", village: "" };
export const layerMatchesLocation = (l, sel) => LEVELS.every(([k]) => !sel[k] || l[k] === sel[k]);

// Cascading location dropdowns (ported from the Bolt LocationPanel). `compact` = phone pill + slide-down sheet.
export function LocationPanel({ layers, selection, onChange, extraRows = [], compact = false }) {
  const [open, setOpen] = useState(!compact);
  const options = useMemo(() => {
    const rows = [...layers, ...extraRows];
    const pick = (key, filter) => [...new Set(rows.filter(filter).map((l) => l[key]).filter(Boolean))].sort();
    return {
      district: pick("district", () => true),
      tehsil: pick("tehsil", (l) => !selection.district || l.district === selection.district),
      ri: pick("ri", (l) => (!selection.district || l.district === selection.district) && (!selection.tehsil || l.tehsil === selection.tehsil)),
      village: pick("village", (l) => (!selection.district || l.district === selection.district) && (!selection.tehsil || l.tehsil === selection.tehsil) && (!selection.ri || l.ri === selection.ri)),
    };
  }, [layers, selection, extraRows]);

  const set = (key, value) => {
    const next = { ...selection, [key]: value };
    const idx = LEVELS.findIndex(([k]) => k === key);
    LEVELS.slice(idx + 1).forEach(([k]) => { next[k] = ""; });
    onChange(next);
    if (compact && key === "village" && value) setOpen(false);
  };
  const active = LEVELS.filter(([k]) => selection[k]).length;
  const matched = layers.filter((l) => layerMatchesLocation(l, selection)).length;
  const tid = compact ? "location-panel-mobile" : "location-panel";

  if (!options.district.length) return null;
  const body = (
    <div className="px-4 pb-4 space-y-2">
      {LEVELS.map(([key, label], i) => {
        const disabled = i > 0 && !selection[LEVELS[i - 1][0]];
        return (
          <label key={key} className="block text-xs">
            <span className="text-muted-foreground">{label}</span>
            <select data-testid={`${compact ? "m-" : ""}location-select-${key}`} value={selection[key]} disabled={disabled} onChange={(e) => set(key, e.target.value)}
              className="mt-0.5 w-full h-10 rounded-xl border bg-card px-2 text-sm disabled:opacity-50">
              <option value="">{disabled ? `Select ${LEVELS[i - 1][1].toLowerCase()} first` : `All ${label.toLowerCase()}s`}</option>
              {options[key].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </label>
        );
      })}
      <div className="flex items-center justify-between text-[11px] text-muted-foreground pt-1">
        <span data-testid={`${compact ? "m-" : ""}location-matched-count`}>{matched} layer{matched === 1 ? "" : "s"} shown</span>
        {active > 0 && <button type="button" data-testid={`${compact ? "m-" : ""}location-clear-button`} onClick={() => onChange(EMPTY_LOCATION)} className="flex items-center gap-1 hover:text-foreground"><X size={11} /> Clear</button>}
      </div>
    </div>
  );
  const summary = selection.village || selection.ri || selection.tehsil || selection.district;
  return (
    <div data-testid={tid} className={`glass rounded-2xl shadow-2xl overflow-hidden ${compact ? "w-full" : "w-64"}`}>
      <button type="button" data-testid={`${tid}-toggle`} onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-2.5 text-[10px] font-mono uppercase tracking-[0.2em] font-semibold text-emerald-800 dark:text-emerald-300">
        <span className="flex items-center gap-2 min-w-0"><MapPinned size={12} /> Location {active > 0 && <span className="px-1.5 rounded-full text-white" style={{ background: "var(--terracotta)" }}>{active}</span>}{compact && summary && !open && <span className="normal-case tracking-normal font-sans text-xs text-foreground truncate">· {summary}</span>}</span>
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {open && body}
    </div>
  );
}

// Toolbar tool: type a village name → see its mapped files → "Show on map" applies it as the location selection.
export function VillageSearch({ open, onClose, rows, layers, onApply }) {
  const [q, setQ] = useState("");
  const [preview, setPreview] = useState(null);
  useEffect(() => { if (open) { setQ(""); setPreview(null); } }, [open]);
  const villages = useMemo(() => {
    const seen = new Map();
    [...rows, ...layers].filter((r) => r.village).forEach((r) => { const k = `${r.district}|${r.tehsil}|${r.ri}|${r.village}`; if (!seen.has(k)) seen.set(k, { district: r.district || "", tehsil: r.tehsil || "", ri: r.ri || "", village: r.village }); });
    return [...seen.values()].sort((a, b) => a.village.localeCompare(b.village));
  }, [rows, layers]);
  const needle = q.trim().toLowerCase();
  const hits = needle ? villages.filter((v) => v.village.toLowerCase().includes(needle) || v.tehsil.toLowerCase().includes(needle)).slice(0, 30) : villages.slice(0, 30);
  const filesOf = (v) => layers.filter((l) => layerMatchesLocation(l, v));
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[2500] bg-black/40 flex items-start justify-center p-4 pt-20" onClick={onClose}>
      <div data-testid="village-search-dialog" className="w-full max-w-md glass rounded-2xl shadow-2xl p-4 fade-up max-h-[75vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3">
          <input autoFocus data-testid="village-search-input" value={q} onChange={(e) => { setQ(e.target.value); setPreview(null); }} placeholder="Type a village name…" className="flex-1 h-10 px-3 rounded-xl border bg-background text-sm" />
          <button type="button" data-testid="village-search-close" onClick={onClose} className="p-2 rounded-lg hover:bg-muted" aria-label="Close"><X size={16} /></button>
        </div>
        <ul className="overflow-auto space-y-1 flex-1">
          {hits.length === 0 && <li className="text-sm text-muted-foreground italic p-2">No village matches "{q}".</li>}
          {hits.map((v) => {
            const files = filesOf(v); const sel = preview && preview.village === v.village && preview.ri === v.ri;
            return (
              <li key={`${v.district}-${v.tehsil}-${v.ri}-${v.village}`}>
                <button type="button" data-testid={`village-result-${v.village}`} onClick={() => setPreview(sel ? null : v)} className={`w-full text-left p-3 rounded-xl transition-colors ${sel ? "bg-emerald-50 dark:bg-emerald-950/40 ring-1 ring-emerald-600" : "hover:bg-muted"}`}>
                  <p className="text-sm font-medium flex items-center justify-between">{v.village} <span className="text-[11px] font-normal text-muted-foreground">{files.length} file{files.length === 1 ? "" : "s"}</span></p>
                  <p className="text-[11px] text-muted-foreground">{[v.district, v.tehsil, v.ri].filter(Boolean).join(" › ")}</p>
                  {sel && (
                    <div className="mt-2 space-y-1" data-testid="village-files">
                      {files.length === 0 && <p className="text-xs text-muted-foreground italic">No files mapped yet</p>}
                      {files.map((l) => <p key={l.id} className="text-xs flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: l.color }} />{l.alias || l.name}<span className="font-mono text-[9px] uppercase text-muted-foreground">{l.kind === "raster" ? "GeoTIFF" : "vector"}</span></p>)}
                    </div>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
        {preview && <button type="button" data-testid="village-show-on-map" onClick={() => { onApply(preview); onClose(); }} className="mt-3 h-10 rounded-full text-sm font-semibold text-white" style={{ background: "var(--terracotta)" }}>Show {preview.village} on map</button>}
      </div>
    </div>,
    document.body,
  );
}
